/**
 * ReportedSaleRow — Pool 2 verdict row inside the Earned accordion.
 * Shows artwork title, "Awaiting payment", dashed Link pill, grey amount.
 * Expands inline when a match suggestion exists.
 */

import { useState } from 'react';
import { T } from '@/styles/tokens';
import { Image } from 'lucide-react';
import type { Verdict } from '@/types/verdict';
import type { MatchSuggestion } from '@/hooks/useMatchSuggestions';

interface ReportedSaleRowProps {
  verdict: Verdict;
  matchSuggestion?: MatchSuggestion | null;
  onLinkMatch?: (verdictId: string, txnId: string) => void;
  onDismissMatch?: (suggestionId: string) => void;
  /** Manual link — opens income linking sheet */
  onLinkIncome?: (verdict: Verdict) => void;
}

function formatDollars(cents: number): string {
  const dollars = Math.round(Math.abs(cents) / 100);
  return `$${dollars.toLocaleString()}`;
}

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function ReportedSaleRow({ verdict, matchSuggestion, onLinkMatch, onDismissMatch, onLinkIncome }: ReportedSaleRowProps) {
  const [expanded, setExpanded] = useState(false);
  const title = verdict.inputs.title || `${verdict.inputs.width}\u00D7${verdict.inputs.height}"`;
  const amount = verdict.soldPrice ?? 0;

  const handleLinkTap = () => {
    setExpanded(prev => !prev);
  };

  const handleConfirmLink = () => {
    if (matchSuggestion && onLinkMatch) {
      onLinkMatch(verdict.id, matchSuggestion.transactionId);
      setExpanded(false);
    }
  };

  const handleDismiss = () => {
    if (matchSuggestion && onDismissMatch) {
      onDismissMatch(matchSuggestion.id);
      setExpanded(false);
    }
  };

  return (
    <div>
      {/* Main row */}
      <div
        onClick={handleLinkTap}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '9px 16px 9px 20px',
          background: T.sand,
          cursor: 'pointer',
        }}
      >
        {/* Icon */}
        <div style={{
          width: 28, height: 28, borderRadius: 8,
          background: 'rgba(26,26,26,0.06)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Image size={14} color={T.textMuted} strokeWidth={1.8} />
        </div>

        {/* Title + subtitle */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 13, fontWeight: 500, color: T.text,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            fontFamily: T.fontFamily,
          }}>
            {title}
          </div>
          <div style={{ fontSize: 11, color: T.gray400, fontFamily: T.fontFamily }}>
            Awaiting payment
          </div>
        </div>

        {/* Pending pill — status indicator, payment hasn't arrived */}
        <span
          onClick={(e) => { e.stopPropagation(); handleLinkTap(); }}
          style={{
            fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
            color: '#BCBCBC', background: 'transparent',
            border: `1.5px dashed #D4D1CC`,
            borderRadius: 26, padding: '2px 8px',
            cursor: 'pointer',
            fontFamily: T.fontFamily,
          }}
        >
          Pending
        </span>

        {/* Amount (grey — unconfirmed) */}
        <span style={{
          fontSize: 14, fontWeight: 600, color: '#BCBCBC',
          fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.3px',
          fontFamily: T.fontFamily, flexShrink: 0,
        }}>
          +{formatDollars(amount)}
        </span>
      </div>

      {/* Inline expansion */}
      {expanded && !matchSuggestion && (
        <div style={{
          margin: '4px 20px 4px 58px',
          background: T.cream, borderRadius: 10, padding: '10px 12px',
          fontSize: 12, color: T.textMuted, fontFamily: T.fontFamily,
        }}>
          No matching payment found yet. This will link automatically when a deposit arrives.
        </div>
      )}
      {expanded && matchSuggestion && (
        <div style={{
          margin: '4px 20px 4px 58px', // indent past icon column
          background: 'rgba(224,122,61,0.04)',
          border: '1px solid rgba(224,122,61,0.13)',
          borderRadius: 10, padding: '10px 12px',
        }}>
          <div style={{ fontSize: 12, color: '#888', fontFamily: T.fontFamily }}>
            {formatDollars(matchSuggestion.txnAmount)} from {matchSuggestion.txnMerchant} &middot; {formatShortDate(matchSuggestion.txnDate)}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button
              onClick={handleConfirmLink}
              style={{
                background: T.primary, color: '#fff',
                fontSize: 12, fontWeight: 600, border: 'none',
                borderRadius: 8, padding: '6px 14px', cursor: 'pointer',
                fontFamily: T.fontFamily,
              }}
            >
              Link payment
            </button>
            <button
              onClick={handleDismiss}
              style={{
                background: 'transparent', color: '#999',
                fontSize: 12, fontWeight: 500,
                border: '1px solid #DDD', borderRadius: 8,
                padding: '6px 12px', cursor: 'pointer',
                fontFamily: T.fontFamily,
              }}
            >
              Not this
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
